import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import faqs from "@/data/faqs.data.json";
import cities from "@/data/cities.data.json";
import {
  Speaker,
  Sparkles,
  Video,
  Music,
  Wrench,
  ArrowRight,
  Check,
  MapPin,
  Phone,
  Mail,
  Zap,
  Clock3,
  Shield,
} from "lucide-react";

const SERVICES = [
  {
    icon: Speaker,
    title: "Impianti Audio",
    desc: "Casse attive RCF, subwoofer, mixer Yamaha, microfoni Shure SM58. Impianti dimensionati per matrimoni, feste private, aziendali e concerti.",
  },
  {
    icon: Music,
    title: "Console DJ e DJ Set",
    desc: "Controller Pioneer DDJ-1000, mixer DJ e attrezzatura professionale per DJ, resident e serate. Con o senza operatore.",
  },
  {
    icon: Sparkles,
    title: "Luci e effetti",
    desc: "Teste mobili beam 230W, par LED RGBW, macchine del fumo Antari. Setup scenografico completo con controllo DMX.",
  },
  {
    icon: Video,
    title: "Video e Proiezione",
    desc: "Videoproiettori laser Full HD 7000 lumen per proiezione grandi eventi, cerimonie, presentazioni aziendali.",
  },
  {
    icon: Wrench,
    title: "Strutture e Truss",
    desc: "Moduli truss quadra alluminio 3m con connettori, americane per aggancio luci e impianti scenografici.",
  },
];

const FAQ = faqs;

const REVIEWS = [
  {
    name: "Giulia M.",
    role: "Sposa — Matrimonio a Como",
    text: "Impianto audio e luci impeccabili per il nostro matrimonio sul lago. Il tecnico è arrivato in orario, tutto ha funzionato alla perfezione. Consigliatissimi!",
  },
  {
    name: "Marco R.",
    role: "Event Manager — Milano",
    text: "Per un evento aziendale in centro a Milano ci serviva un service audio+video professionale. Spectrum ha risolto tutto in un preventivo con foto e prezzi chiari.",
  },
  {
    name: "DJ Andrea",
    role: "Resident DJ",
    text: "Noleggio abitualmente console e impianto per le mie serate. Materiale sempre in ottime condizioni e prezzi onesti.",
  },
];

export default function Landing() {
  return (
    <>
      <SEO
        title="Spectrum — Noleggio Audio Luci Video a Como e Milano | Impianti, DJ Set, Console DJ"
        description="Service professionale di noleggio impianti audio, luci, video e console DJ Pioneer a Como e Milano. Preventivo online in un minuto per matrimoni, feste ed eventi aziendali."
        canonical="https://www.spectrumservice.it/"
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-25 mix-blend-screen"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?crop=entropy&cs=srgb&fm=jpg&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-zinc-950/85 to-zinc-950" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-28">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-rose-500/40 bg-rose-500/10 text-rose-300 text-[10px] font-mono uppercase tracking-widest mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
            Como · Milano · Lombardia
          </div>
          <h1 className="font-heading text-4xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tight text-white leading-[0.95] max-w-4xl">
            Noleggio audio, luci e video professionali a{" "}
            <span className="text-rose-500">Como e Milano</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-zinc-400 max-w-2xl">
            Spectrum è il service che accende ogni tuo evento. Impianti audio,
            console DJ Pioneer, luci scenografiche e videoproiezione a noleggio
            con preventivo online in un minuto.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/catalogo"
              className="inline-flex items-center gap-2 min-h-[52px] px-6 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-heading font-black uppercase tracking-wider text-sm shadow-lg shadow-rose-900/40 transition"
            >
              Guarda il catalogo <ArrowRight size={16} />
            </Link>
            <a
              href="tel:+393888143630"
              className="inline-flex items-center gap-2 min-h-[52px] px-6 rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white font-semibold text-sm uppercase tracking-wider transition"
            >
              <Phone size={16} /> Chiama subito
            </a>
          </div>

          {/* Trust bar */}
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl">
            {[
              { icon: Zap, label: "Preventivo istantaneo" },
              { icon: Clock3, label: "Noleggio multi-giorno" },
              { icon: Shield, label: "Materiale professionale" },
              { icon: MapPin, label: "Como e Milano" },
            ].map((t, i) => (
              <div
                key={i}
                className="flex items-center gap-2 text-zinc-400 text-xs sm:text-sm"
              >
                <t.icon size={16} className="text-rose-500 shrink-0" />
                <span>{t.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="text-[10px] font-mono uppercase tracking-widest text-rose-500 mb-2">
          I nostri servizi
        </div>
        <h2 className="font-heading text-3xl sm:text-5xl font-black uppercase tracking-tight text-white leading-tight">
          Tutto il <span className="text-rose-500">service</span> di cui hai
          bisogno
        </h2>
        <p className="text-zinc-400 mt-3 max-w-2xl text-sm sm:text-base">
          Dall'impianto audio potente al DJ set completo, dalle luci alle
          strutture truss: un unico partner per il tuo evento a Como, Milano e
          tutta la Lombardia.
        </p>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICES.map((s) => (
            <div
              key={s.title}
              className="bg-zinc-900/90 border border-zinc-800 rounded-xl p-5 hover:border-rose-900/60 transition"
            >
              <div className="w-11 h-11 rounded-lg bg-rose-600/15 border border-rose-500/25 text-rose-400 flex items-center justify-center mb-3">
                <s.icon size={20} />
              </div>
              <h3 className="font-heading font-bold text-white text-lg leading-tight">
                {s.title}
              </h3>
              <p className="text-zinc-400 text-sm mt-2 leading-relaxed">
                {s.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-gradient-to-b from-zinc-950 to-zinc-900/50 border-y border-zinc-900 py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[10px] font-mono uppercase tracking-widest text-rose-500 mb-2">
            Come funziona
          </div>
          <h2 className="font-heading text-3xl sm:text-5xl font-black uppercase tracking-tight text-white leading-tight">
            Preventivo in <span className="text-rose-500">3 step</span>
          </h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              {
                n: "01",
                title: "Scegli le date",
                desc: "Seleziona data inizio e fine del tuo evento. Il sistema calcola i giorni e verifica la disponibilità in tempo reale.",
              },
              {
                n: "02",
                title: "Componi il tuo setup",
                desc: "Sfoglia il catalogo di audio, luci, DJ, video e strutture. Aggiungi articoli e quantità al tuo preventivo.",
              },
              {
                n: "03",
                title: "Invia la richiesta",
                desc: "Compila i tuoi contatti e invia. Ti rispondiamo in poche ore con un PDF professionale.",
              },
            ].map((step) => (
              <div key={step.n} className="relative">
                <div className="font-mono font-black text-6xl text-zinc-800 leading-none">
                  {step.n}
                </div>
                <h3 className="font-heading font-bold text-white text-lg mt-2">
                  {step.title}
                </h3>
                <p className="text-zinc-400 text-sm mt-2">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="text-[10px] font-mono uppercase tracking-widest text-rose-500 mb-2">
          Cosa dicono di noi
        </div>
        <h2 className="font-heading text-3xl sm:text-5xl font-black uppercase tracking-tight text-white leading-tight">
          Eventi <span className="text-rose-500">illuminati</span> dai nostri
          impianti
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {REVIEWS.map((r, i) => (
            <div
              key={i}
              className="bg-zinc-900/90 border border-zinc-800 rounded-xl p-5"
            >
              <p className="text-zinc-300 text-sm leading-relaxed">
                &ldquo;{r.text}&rdquo;
              </p>
              <div className="mt-4 pt-4 border-t border-zinc-800">
                <div className="font-heading font-bold text-white text-sm">
                  {r.name}
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">
                  {r.role}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Zone servite — internal links to city landing pages (SEO) */}
      <section
        data-testid="cities-section"
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 border-t border-zinc-900"
      >
        <div className="text-[10px] font-mono uppercase tracking-widest text-rose-500 mb-2">
          Zone servite
        </div>
        <h2 className="font-heading text-3xl sm:text-5xl font-black uppercase tracking-tight text-white leading-tight">
          Noleggio audio, luci e video in{" "}
          <span className="text-rose-500">Lombardia</span>
        </h2>
        <p className="mt-3 text-sm sm:text-base text-zinc-400 max-w-2xl">
          Sede a Como. Copriamo Milano, Lecco, Varese, Monza e Brianza, e su
          richiesta tutta la Lombardia + Canton Ticino.
        </p>
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {cities.map((c) => (
            <Link
              key={c.slug}
              data-testid={`landing-city-link-${c.slug}`}
              to={`/${c.slug}`}
              className="group bg-zinc-900/60 border border-zinc-800 hover:border-rose-500/50 rounded-xl p-4 transition"
            >
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 group-hover:text-rose-400 transition">
                Provincia {c.province}
              </div>
              <div className="font-heading font-black text-white text-lg mt-1 group-hover:text-rose-400 transition">
                {c.name}
              </div>
              <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-zinc-600 flex items-center gap-1">
                Scopri <ArrowRight size={11} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-zinc-900/40 border-y border-zinc-900 py-14 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[10px] font-mono uppercase tracking-widest text-rose-500 mb-2">
            Domande frequenti
          </div>
          <h2 className="font-heading text-3xl sm:text-5xl font-black uppercase tracking-tight text-white leading-tight">
            FAQ noleggio audio, luci e DJ
          </h2>
          <div className="mt-8 space-y-3">
            {FAQ.map((f, i) => (
              <details
                key={i}
                className="group bg-zinc-900/90 border border-zinc-800 rounded-xl overflow-hidden"
              >
                <summary className="cursor-pointer px-5 py-4 font-heading font-bold text-white text-sm sm:text-base flex items-center justify-between gap-3 list-none">
                  <span>{f.q}</span>
                  <ArrowRight
                    size={16}
                    className="shrink-0 text-rose-500 transition-transform group-open:rotate-90"
                  />
                </summary>
                <div className="px-5 pb-4 pt-0 text-zinc-400 text-sm leading-relaxed border-t border-zinc-800">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
          {/* FAQ JSON-LD */}
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: FAQ.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              }),
            }}
          />
        </div>
      </section>

      {/* CTA + Contatti */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="bg-gradient-to-br from-rose-950 via-zinc-950 to-black border border-rose-900/50 rounded-2xl p-8 sm:p-12 relative overflow-hidden">
          <div className="relative">
            <h2 className="font-heading text-3xl sm:text-5xl font-black uppercase tracking-tight text-white leading-tight max-w-2xl">
              Pronto per il tuo prossimo{" "}
              <span className="text-rose-500">evento</span>?
            </h2>
            <p className="text-zinc-400 mt-3 max-w-xl text-sm sm:text-base">
              Vai al catalogo, componi il preventivo in un minuto oppure
              chiamaci per una consulenza gratuita.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/catalogo"
                className="inline-flex items-center gap-2 min-h-[52px] px-6 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-heading font-black uppercase tracking-wider text-sm shadow-lg shadow-rose-900/40 transition"
              >
                Vai al catalogo <ArrowRight size={16} />
              </Link>
              <a
                href="tel:+393888143630"
                className="inline-flex items-center gap-2 min-h-[52px] px-6 rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white font-semibold text-sm uppercase tracking-wider transition"
              >
                <Phone size={16} /> 388 814 3630
              </a>
              <a
                href="mailto:spectrumvalentin@gmail.com"
                className="inline-flex items-center gap-2 min-h-[52px] px-6 rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white font-semibold text-sm uppercase tracking-wider transition"
              >
                <Mail size={16} /> Scrivici
              </a>
            </div>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-xs text-zinc-500 font-mono uppercase tracking-widest">
              <span className="flex items-center gap-1.5">
                <Check size={12} className="text-emerald-400" /> P.IVA 04249740137
              </span>
              <span className="flex items-center gap-1.5">
                <Check size={12} className="text-emerald-400" /> Regime forfettario
              </span>
              <span className="flex items-center gap-1.5">
                <Check size={12} className="text-emerald-400" /> Fatturazione elettronica
              </span>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
